import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Crown, Lock, Check, Sparkles, Coins, Shirt, Clock, Gift } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { Avatar, AVATAR_ICONS, RARITY_COLORS } from "@/lib/cosmetics";

function RewardCell({ reward, coins, unlocked, claimed, canClaim, onClaim, catalog }) {
  if (!reward && !coins) return <div className="h-24 rounded-xl bg-black/20 border border-dashed border-white/10" />;
  return (
    <div className={`h-24 rounded-xl border flex flex-col items-center justify-center gap-1 relative ${claimed ? "border-emerald-500/40 bg-emerald-500/5" : unlocked ? "border-white/15 bg-black/30" : "border-white/10 bg-black/20 opacity-60"}`}>
      {reward ? (
        <>
          <Avatar avatarId={reward.type === "avatar" ? reward.id : null} frameId={reward.type === "frame" ? reward.id : null} catalog={catalog} size={36} />
          <span className={`text-[10px] ${RARITY_COLORS[reward.rarity]?.split(" ")[0] || "text-gray-300"}`}>{reward.name}</span>
        </>
      ) : (
        <><Coins className="w-6 h-6 text-amber-400" /><span className="text-[10px] text-amber-300 font-mono">+{coins.toLocaleString()}</span></>
      )}
      {claimed ? (
        <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center"><Check className="w-3 h-3 text-black" /></div>
      ) : canClaim ? (
        <button onClick={onClaim} className="absolute inset-0 rounded-xl bg-black/50 opacity-0 hover:opacity-100 transition-opacity duration-200 flex items-center justify-center text-xs font-semibold">Claim</button>
      ) : !unlocked ? (
        <Lock className="absolute top-1 right-1 w-3.5 h-3.5 text-gray-500" />
      ) : null}
    </div>
  );
}

export default function BattlePass() {
  const { user, setUser, refresh } = useAuth();
  const { t } = useT();
  const [data, setData] = useState(null);

  const load = async () => {
    try { const { data: d } = await api.get("/battlepass"); setData(d); }
    catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const buyPremium = async () => {
    try { const { data: d } = await api.post("/battlepass/buy-premium"); setUser((u) => ({ ...u, coins: d.coins })); await load(); toast.success("Premium pass unlocked! 🎉"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const claim = async (tier, track) => {
    try {
      const { data: d } = await api.post("/battlepass/claim", { tier, track });
      if (d.coins_reward) setUser((u) => ({ ...u, coins: d.coins }));
      await load();
      toast.success(d.cosmetic ? `Unlocked ${d.cosmetic.name}!` : `+${d.coins_reward.toLocaleString()} coins`);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const equip = async (slot, id) => {
    try {
      await api.post("/battlepass/equip", { slot, cosmetic_id: id });
      await refresh();
      toast.success(id ? t("equipped") : t("unequip"));
    } catch (e) { toast.error(apiErr(e)); }
  };
  const claimChest = async () => {
    try {
      const { data: d } = await api.post("/battlepass/claim-chest");
      setUser((u) => ({ ...u, coins: d.coins }));
      await load();
      toast.success(`Season chest! +${d.coins_reward.toLocaleString()} coins + ${d.cosmetic.name} 🎁`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!data) return <div className="p-8 text-gray-500">{t("loading")}</div>;

  const pct = Math.round((data.xp_into_tier / data.xp_per_tier) * 100);
  const ownedCosmetics = data.owned.map((id) => ({ id, ...data.catalog[id] }));
  const endsMs = data.season_ends_at ? new Date(data.season_ends_at) - new Date() : 0;
  const daysLeft = Math.max(0, Math.floor(endsMs / 8.64e7));
  const hoursLeft = Math.max(0, Math.floor((endsMs % 8.64e7) / 3.6e6));
  const chest = data.chest || {};

  return (
    <div className="p-4 lg:p-8">
      <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-4 mb-6">
        <div>
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/15 border border-purple-500/30 text-purple-300 text-xs font-semibold mb-3">
            <Sparkles className="w-3.5 h-3.5" /> {data.season_name}
          </span>
          <h1 className="font-display text-4xl sm:text-5xl font-black flex items-center gap-3"><Crown className="w-9 h-9 text-amber-400" /> {t("battlepass_title")}</h1>
        </div>
        {data.premium ? (
          <span className="px-4 py-2 rounded-xl bg-amber-500/15 border border-amber-500/40 text-amber-300 font-semibold text-sm">✨ {t("premium_unlocked")}</span>
        ) : (
          <button data-testid="buy-premium-button" onClick={buyPremium}
            className="px-5 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-purple-600 font-bold hover:opacity-90 transition-opacity duration-200 flex items-center gap-2">
            <Sparkles className="w-4 h-4" /> {t("unlock_premium")} · {data.premium_price.toLocaleString()} <Coins className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* progress */}
      <div className="glass rounded-2xl p-5 border border-white/10 mb-6">
        <div className="flex justify-between mb-2 text-sm">
          <span className="font-semibold">{t("tier")} {data.current_tier} / {data.max_tier}</span>
          <span className="text-gray-500 font-mono">{data.xp_into_tier}/{data.xp_per_tier} XP</span>
        </div>
        <div className="h-3 rounded-full bg-black/50 overflow-hidden"><div className="h-full bg-gradient-to-r from-amber-400 to-purple-500 transition-all duration-500" style={{ width: `${pct}%` }} /></div>
        <p className="text-xs text-gray-500 mt-2">Earn XP by playing any game to climb tiers and unlock cosmetics.</p>
      </div>

      {/* season countdown + chest */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="glass rounded-2xl p-5 border border-white/10 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-cyan-500/15 flex items-center justify-center"><Clock className="w-6 h-6 text-cyan-400" /></div>
          <div>
            <p className="text-sm text-gray-500">{t("season_ends")}</p>
            <p className="font-mono font-black text-xl">{daysLeft}d {hoursLeft}h</p>
          </div>
        </div>
        <div className={`glass rounded-2xl p-5 border flex items-center justify-between lg:col-span-2 ${chest.unlocked && !chest.claimed ? "border-amber-500/50 glow-coins" : "border-white/10"}`}>
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${chest.unlocked ? "bg-amber-500/20" : "bg-white/5"}`}>
              <Gift className={`w-6 h-6 ${chest.unlocked ? "text-amber-400" : "text-gray-500"}`} />
            </div>
            <div>
              <p className="font-semibold">{t("season_chest")}</p>
              <p className="text-sm text-gray-500">{chest.claimed ? "Claimed ✓" : chest.unlocked ? `+${(chest.coins || 0).toLocaleString()} coins + ${chest.cosmetic?.name}` : t("chest_locked")}</p>
            </div>
          </div>
          <button data-testid="claim-chest-button" onClick={claimChest} disabled={!chest.unlocked || chest.claimed}
            className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-purple-600 font-semibold hover:opacity-90 transition-opacity duration-200 disabled:opacity-40 flex items-center gap-2">
            {chest.claimed ? <Check className="w-4 h-4" /> : <Gift className="w-4 h-4" />} {t("claim_chest")}
          </button>
        </div>
      </div>

      {/* track */}
      <div className="glass rounded-2xl p-5 border border-white/10 mb-8 overflow-x-auto">
        <div className="flex gap-3 min-w-max">
          {data.tiers.map((tr) => (
            <div key={tr.tier} className="w-28 flex-shrink-0">
              <div className={`text-center mb-2 font-mono font-bold ${tr.unlocked ? "text-emerald-400" : "text-gray-500"}`}>{tr.tier}</div>
              <div className="space-y-2">
                <div>
                  <p className="text-[10px] text-gray-500 mb-1 text-center uppercase">{t("free")}</p>
                  <RewardCell reward={tr.free_reward} coins={tr.free_coins} unlocked={tr.unlocked} claimed={tr.claimed_free}
                    canClaim={tr.unlocked && !tr.claimed_free && (tr.free_reward || tr.free_coins)} onClaim={() => claim(tr.tier, "free")} catalog={data.catalog} />
                </div>
                <div>
                  <p className="text-[10px] text-amber-500/70 mb-1 text-center uppercase">{t("premium")}</p>
                  <RewardCell reward={tr.premium_reward} unlocked={tr.unlocked && data.premium} claimed={tr.claimed_premium}
                    canClaim={tr.unlocked && data.premium && !tr.claimed_premium && tr.premium_reward} onClaim={() => claim(tr.tier, "premium")} catalog={data.catalog} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* locker */}
      <h2 className="font-display text-2xl font-bold mb-4 flex items-center gap-2"><Shirt className="w-6 h-6 text-purple-400" /> {t("locker")}</h2>
      {ownedCosmetics.length === 0 ? (
        <p className="text-gray-500">Claim rewards from the track to fill your locker.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {ownedCosmetics.map((c) => {
            const equipped = (c.type === "avatar" && data.equipped_avatar === c.id) || (c.type === "frame" && data.equipped_frame === c.id);
            return (
              <div key={c.id} className={`glass rounded-2xl p-4 border text-center ${equipped ? "border-emerald-500/50" : "border-white/10"}`}>
                <div className="flex justify-center mb-2"><Avatar avatarId={c.type === "avatar" ? c.id : null} frameId={c.type === "frame" ? c.id : null} catalog={data.catalog} size={48} /></div>
                <p className={`text-sm font-medium ${RARITY_COLORS[c.rarity]?.split(" ")[0]}`}>{c.name}</p>
                <p className="text-[10px] text-gray-500 uppercase mb-2">{c.type} · {c.rarity}</p>
                <button data-testid={`equip-${c.id}`} onClick={() => equip(c.type, equipped ? null : c.id)}
                  className={`w-full py-1.5 rounded-lg text-xs font-semibold transition-colors duration-200 ${equipped ? "bg-emerald-500/20 text-emerald-300" : "bg-white/10 hover:bg-white/15"}`}>
                  {equipped ? t("equipped") : t("equip")}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
